import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarBlank, EnvelopeSimple, Phone, Users, PlugsConnected } from "@phosphor-icons/react";
import { fetchBookings } from "../../lib/api";

function StatusBadge({ status }) {
  const styles = {
    pending: "border-amber-400/40 text-amber-300 bg-amber-400/5",
    confirmed: "border-emerald-400/40 text-emerald-300 bg-emerald-400/5",
    cancelled: "border-red-400/40 text-red-300 bg-red-400/5",
  };
  const label = {
    pending: "In attesa",
    confirmed: "Confermata",
    cancelled: "Annullata",
  };
  return (
    <span
      className={`inline-flex items-center text-xs uppercase tracking-[0.15em] px-2.5 py-1 border ${
        styles[status] || "border-white/20 text-white/60"
      }`}
    >
      {label[status] || status}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function BookingsPage() {
  const [bookings, setBookings] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBookings()
      .then((data) => setBookings(Array.isArray(data) ? data : []))
      .catch((err) => setError(err?.message || "Errore di caricamento"));
  }, []);

  return (
    <div className="p-8 lg:p-12" data-testid="admin-bookings-page">
      <header className="mb-10">
        <div className="text-xs uppercase tracking-[0.25em] text-[#C5A059] mb-2">
          Dashboard
        </div>
        <h1 className="font-serif text-4xl lg:text-5xl leading-tight">Prenotazioni</h1>
        <p className="text-white/50 mt-3 text-sm">
          Tutte le richieste di prenotazione ricevute dal form pubblico.
        </p>
      </header>

      {/* PMS integration status banner */}
      <div className="mb-8 border border-[#C5A059]/30 bg-[#C5A059]/5 px-5 py-4 flex items-start gap-4" data-testid="pms-banner">
        <div className="w-10 h-10 rounded-full border border-[#C5A059]/40 flex items-center justify-center text-[#C5A059] shrink-0">
          <PlugsConnected size={18} weight="light" />
        </div>
        <div className="flex-1">
          <div className="text-sm text-[#FFFFF0] font-medium">Motore di prenotazione: richieste via form</div>
          <div className="text-xs text-white/55 mt-1 leading-relaxed">
            Le prenotazioni arrivano dal form del sito e appaiono qui sotto. L'infrastruttura è pronta
            per integrare <strong className="text-white/80">Simple Booking</strong> o il
            <strong className="text-white/80"> Booking.com Widget</strong>: disponibilità, prezzi
            dinamici e conferma immediata. Servono solo le credenziali del PMS.
          </div>
        </div>
      </div>

      {error && (
        <div className="border border-red-500/40 bg-red-500/5 text-red-300 px-4 py-3 text-sm mb-6">
          {error}
        </div>
      )}

      {!bookings && !error && (
        <div className="text-white/40 text-sm">Caricamento…</div>
      )}

      {bookings && bookings.length === 0 && (
        <div className="border border-white/10 px-8 py-16 text-center text-white/50">
          <CalendarBlank size={32} weight="light" className="mx-auto mb-4 text-white/30" />
          Nessuna prenotazione ancora ricevuta.
          <div className="text-xs text-white/30 mt-2">
            Le richieste inviate dal form di prenotazione appariranno qui.
          </div>
        </div>
      )}

      {bookings && bookings.length > 0 && (
        <div className="border border-white/10 overflow-hidden">
          <table className="w-full" data-testid="admin-bookings-table">
            <thead>
              <tr className="border-b border-white/10 text-xs uppercase tracking-[0.15em] text-white/50">
                <th className="text-left font-normal px-6 py-3">Ospite</th>
                <th className="text-left font-normal px-6 py-3">Date</th>
                <th className="text-left font-normal px-6 py-3">Ospiti</th>
                <th className="text-left font-normal px-6 py-3">Camera</th>
                <th className="text-left font-normal px-6 py-3">Stato</th>
                <th className="text-right font-normal px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr
                  key={b.id}
                  className="border-b border-white/5 last:border-b-0 hover:bg-white/[0.02]"
                  data-testid={`admin-booking-row-${b.id}`}
                >
                  <td className="px-6 py-4">
                    <div className="font-medium">{b.name}</div>
                    <div className="text-xs text-white/40 flex items-center gap-1 mt-1">
                      <EnvelopeSimple size={12} weight="light" />
                      {b.email}
                    </div>
                    {b.phone && (
                      <div className="text-xs text-white/40 flex items-center gap-1">
                        <Phone size={12} weight="light" />
                        {b.phone}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div>{formatDate(b.check_in)}</div>
                    <div className="text-xs text-white/40">→ {formatDate(b.check_out)}</div>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="inline-flex items-center gap-1 text-white/70">
                      <Users size={14} weight="light" />
                      {b.guests}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-white/70">
                    {b.room_slug || <span className="text-white/30">—</span>}
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={b.status} />
                  </td>
                  <td className="px-6 py-4 text-right">
                    <Link
                      to={`/admin/bookings/${b.id}`}
                      className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-[#C5A059] hover:text-[#D4AF37]"
                    >
                      Dettagli <ArrowRight size={12} weight="bold" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
